'use client';

import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Plus, Search, Filter, X, ClipboardList,
  Edit2, Trash2, ChevronUp, ChevronDown, Clock, Calendar,
} from 'lucide-react';
import { INTERVENTIONS, CLIENTS, CONNECTION_TYPE_COLORS, STATUS_COLORS, type Intervention, type ConnectionType, type InterventionStatus,  } from '@/lib/data';
import InterventionForm from './InterventionForm';
import ConfirmModal from '@/components/ui/ConfirmModal';

const ALL_CONN_TYPES: ConnectionType[] = ['Na lokaciji', 'VPN', 'VNC', 'RDP', 'AnyDesk', 'TeamViewer'];
const ALL_STATUSES: InterventionStatus[] = ['Završena', 'U toku', 'Zakazana'];
const PAGE_SIZES = [10, 20, 50];

type SortKey = 'date' | 'clientName' | 'connectionType' | 'duration' | 'status';

function formatDate(d: string) {
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
}

function formatDuration(min: number) {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

export default function InterventionLogScreen() {
  const [interventions, setInterventions] = useState<Intervention[]>(INTERVENTIONS);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterType, setFilterType] = useState<ConnectionType | ''>('');
  const [filterStatus, setFilterStatus] = useState<InterventionStatus | ''>('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [formOpen, setFormOpen] = useState(false);
  const [editingInt, setEditingInt] = useState<Intervention | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Intervention | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return interventions
      .filter(i => {
        if (q && !i.clientName.toLowerCase().includes(q) && !i.description.toLowerCase().includes(q)) return false;
        if (filterClient && i.clientId !== filterClient) return false;
        if (filterType && i.connectionType !== filterType) return false;
        if (filterStatus && i.status !== filterStatus) return false;
        if (filterDateFrom && i.date < filterDateFrom) return false;
        if (filterDateTo && i.date > filterDateTo) return false;
        return true;
      })
      .sort((a, b) => {
        let av = '', bv = '';
        if (sortKey === 'date') { av = a.date + a.time; bv = b.date + b.time; }
        else if (sortKey === 'clientName') { av = a.clientName; bv = b.clientName; }
        else if (sortKey === 'connectionType') { av = a.connectionType; bv = b.connectionType; }
        else if (sortKey === 'duration') { av = String(a.duration).padStart(5, '0'); bv = String(b.duration).padStart(5, '0'); }
        else if (sortKey === 'status') { av = a.status; bv = b.status; }
        return sortDir === 'asc' ? av.localeCompare(bv, 'sr') : bv.localeCompare(av, 'sr');
      });
  }, [interventions, search, filterClient, filterType, filterStatus, filterDateFrom, filterDateTo, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const hasActiveFilters = filterClient || filterType || filterStatus || filterDateFrom || filterDateTo;

  function clearFilters() {
    setFilterClient('');
    setFilterType('');
    setFilterStatus('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setPage(1);
  }

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
    setPage(1);
  }

  function toggleRow(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === paginated.length) setSelected(new Set());
    else setSelected(new Set(paginated.map(i => i.id)));
  }

  function handleSave(data: Intervention) {
    // Backend integration: POST /api/interventions or PUT /api/interventions/:id
    if (editingInt) {
      setInterventions(prev => prev.map(i => i.id === data.id ? data : i));
      toast.success('Intervencija je ažurirana.');
    } else {
      setInterventions(prev => [data, ...prev]);
      toast.success('Intervencija je evidentirana.');
    }
    setFormOpen(false);
    setEditingInt(null);
  }

  function handleDelete() {
    setDeleteLoading(true);
    // Backend integration: DELETE /api/interventions/:id
    setTimeout(() => {
      setInterventions(prev => prev.filter(i => i.id !== deleteTarget?.id));
      toast.success('Intervencija je obrisana.');
      setDeleteTarget(null);
      setDeleteLoading(false);
    }, 500);
  }

  function handleBulkDelete() {
    setDeleteLoading(true);
    // Backend integration: DELETE /api/interventions/bulk
    setTimeout(() => {
      const count = selected.size;
      setInterventions(prev => prev.filter(i => !selected.has(i.id)));
      setSelected(new Set());
      toast.success(`${count} ${count === 1 ? 'intervencija obrisana' : 'intervencija obrisano'}.`);
      setBulkDeleteOpen(false);
      setDeleteLoading(false);
    }, 500);
  }

  const SortIcon = ({ k }: { k: SortKey }) => (
    <span className="ml-1 inline-flex flex-col" style={{ color: sortKey === k ? 'hsl(var(--accent))' : 'hsl(var(--muted-foreground))' }}>
      {sortKey === k
        ? (sortDir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)
        : <span className="text-xs">↕</span>
      }
    </span>
  );

  return (
    <div className="flex flex-col h-full" style={{ minHeight: '100vh' }}>
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-5 border-b sticky top-0 z-10"
        style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}
      >
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
            Evidencija intervencija
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
            {filtered.length} {filtered.length === 1 ? 'intervencija' : 'intervencija'} ·{' '}
            <span className="tabular-nums">
              {interventions.filter(i => i.status === 'U toku').length}
            </span> u toku
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => { setEditingInt(null); setFormOpen(true); }}
        >
          <Plus size={16} />
          Nova intervencija
        </button>
      </div>

      {/* Toolbar */}
      <div
        className="flex flex-wrap items-center gap-3 px-6 py-3 border-b"
        style={{ borderColor: 'hsl(var(--border))', backgroundColor: 'hsl(var(--muted) / 0.3)' }}
      >
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'hsl(var(--muted-foreground))' }} />
          <input
            type="text"
            placeholder="Pretraži opis, klijenta..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="input-field pl-9 pr-8 text-sm py-2 w-64"
          />
          {search && (
            <button onClick={() => { setSearch(''); setPage(1); }} className="absolute right-2 top-1/2 -translate-y-1/2">
              <X size={13} style={{ color: 'hsl(var(--muted-foreground))' }} />
            </button>
          )}
        </div>

        <button
          className={`btn-secondary text-sm py-2 gap-1.5 ${showFilters ? 'border-blue-400' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
          style={showFilters ? { borderColor: 'hsl(var(--accent))' } : {}}
        >
          <Filter size={14} />
          Filteri
          {hasActiveFilters && (
            <span
              className="w-4 h-4 rounded-full text-xs flex items-center justify-center font-bold"
              style={{ backgroundColor: 'hsl(var(--accent))', color: 'white' }}
            >
              !
            </span>
          )}
        </button>

        {hasActiveFilters && (
          <button className="btn-ghost text-sm py-2" onClick={clearFilters}>
            <X size={13} />
            Resetuj filtere
          </button>
        )}

        <div className="ml-auto flex items-center gap-2 text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
          <Clock size={14} />
          <span>
            Ukupno: <strong className="tabular-nums" style={{ color: 'hsl(var(--foreground))' }}>
              {filtered.reduce((s, i) => s + i.duration, 0)}
            </strong> min
          </span>
        </div>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div
          className="flex flex-wrap gap-4 px-6 py-4 border-b animate-slide-up"
          style={{ borderColor: 'hsl(var(--border))', backgroundColor: 'hsl(var(--muted) / 0.2)' }}
        >
          <div>
            <label className="label text-xs mb-1">Klijent</label>
            <select
              className="input-field text-sm py-1.5 pr-8 min-w-[180px]"
              value={filterClient}
              onChange={e => { setFilterClient(e.target.value); setPage(1); }}
            >
              <option value="">Svi klijenti</option>
              {CLIENTS.map(c => (
                <option key={`filter-client-${c.id}`} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs mb-1">Tip veze</label>
            <select
              className="input-field text-sm py-1.5 pr-8 min-w-[140px]"
              value={filterType}
              onChange={e => { setFilterType(e.target.value as ConnectionType | ''); setPage(1); }}
            >
              <option value="">Svi tipovi</option>
              {ALL_CONN_TYPES.map(t => (
                <option key={`filter-type-${t}`} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs mb-1">Status</label>
            <select
              className="input-field text-sm py-1.5 pr-8 min-w-[130px]"
              value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value as InterventionStatus | ''); setPage(1); }}
            >
              <option value="">Svi statusi</option>
              {ALL_STATUSES.map(s => (
                <option key={`filter-status-${s}`} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs mb-1">Datum od</label>
            <input
              type="date"
              className="input-field text-sm py-1.5"
              value={filterDateFrom}
              onChange={e => { setFilterDateFrom(e.target.value); setPage(1); }}
            />
          </div>
          <div>
            <label className="label text-xs mb-1">Datum do</label>
            <input
              type="date"
              className="input-field text-sm py-1.5"
              value={filterDateTo}
              onChange={e => { setFilterDateTo(e.target.value); setPage(1); }}
            />
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          className="flex items-center gap-3 px-6 py-2.5 border-b animate-slide-up"
          style={{ backgroundColor: 'hsl(217 91% 60% / 0.06)', borderColor: 'hsl(var(--accent) / 0.3)' }}
        >
          <span className="text-sm font-medium" style={{ color: 'hsl(var(--accent))' }}>
            {selected.size} {selected.size === 1 ? 'intervencija izabrana' : 'intervencija izabrano'}
          </span>
          <button
            className="btn-destructive text-xs py-1.5 px-3"
            onClick={() => setBulkDeleteOpen(true)}
          >
            <Trash2 size={13} />
            Obriši izabrane
          </button>
          <button className="btn-ghost text-xs py-1.5 px-3" onClick={() => setSelected(new Set())}>
            Poništi selekciju
          </button>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[900px]">
          <thead
            style={{ backgroundColor: 'hsl(var(--muted) / 0.6)', borderBottom: '1px solid hsl(var(--border))' }}
          >
            <tr>
              <th className="table-header w-10">
                <input
                  type="checkbox"
                  checked={paginated.length > 0 && selected.size === paginated.length}
                  onChange={toggleAll}
                  className="rounded"
                  aria-label="Izaberi sve"
                />
              </th>
              <th className="table-header cursor-pointer" onClick={() => toggleSort('date')}>
                Datum i vreme <SortIcon k="date" />
              </th>
              <th className="table-header cursor-pointer" onClick={() => toggleSort('clientName')}>
                Klijent <SortIcon k="clientName" />
              </th>
              <th className="table-header cursor-pointer" onClick={() => toggleSort('connectionType')}>
                Tip veze <SortIcon k="connectionType" />
              </th>
              <th className="table-header">Opis intervencije</th>
              <th className="table-header cursor-pointer" onClick={() => toggleSort('duration')}>
                Trajanje <SortIcon k="duration" />
              </th>
              <th className="table-header">Oprema</th>
              <th className="table-header cursor-pointer" onClick={() => toggleSort('status')}>
                Status <SortIcon k="status" />
              </th>
              <th className="table-header w-24"></th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <ClipboardList size={36} style={{ color: 'hsl(var(--muted-foreground))' }} />
                    <p className="font-medium" style={{ color: 'hsl(var(--foreground))' }}>
                      {search || hasActiveFilters ? 'Nema intervencija za zadate filtere' : 'Nema evidentiranih intervencija'}
                    </p>
                    <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
                      {search || hasActiveFilters
                        ? 'Pokušajte sa drugačijim filterima ili pretragom.' :'Kliknite "Nova intervencija" da evidentirate prvu intervenciju.'}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map(inv => {
                const ctColor = CONNECTION_TYPE_COLORS[inv.connectionType];
                const stColor = STATUS_COLORS[inv.status];
                const isSelected = selected.has(inv.id);
                return (
                  <tr
                    key={inv.id}
                    className={`table-row group ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <td className="table-cell w-10">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(inv.id)}
                        className="rounded"
                        aria-label={`Izaberi intervenciju ${inv.id}`}
                      />
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1.5">
                        <Calendar size={13} style={{ color: 'hsl(var(--muted-foreground))' }} />
                        <div>
                          <p className="text-sm font-medium tabular-nums">
                            {formatDate(inv.date)}
                          </p>
                          <p className="text-xs tabular-nums" style={{ color: 'hsl(var(--muted-foreground))' }}>
                            {inv.time}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold flex-shrink-0"
                          style={{ backgroundColor: 'hsl(var(--accent) / 0.12)', color: 'hsl(var(--accent))' }}
                        >
                          {inv.clientName.slice(0, 2).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium truncate max-w-[140px]">{inv.clientName}</span>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${ctColor.bg} ${ctColor.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${ctColor.dot}`} />
                        {inv.connectionType}
                      </span>
                    </td>
                    <td className="table-cell max-w-[260px]">
                      <p className="text-sm line-clamp-2 leading-snug" title={inv.description}>
                        {inv.description}
                      </p>
                      {inv.notes && (
                        <p className="text-xs mt-0.5 italic line-clamp-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
                          {inv.notes}
                        </p>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className="flex items-center gap-1 text-sm tabular-nums">
                        <Clock size={12} style={{ color: 'hsl(var(--muted-foreground))' }} />
                        {formatDuration(inv.duration)}
                      </span>
                    </td>
                    <td className="table-cell">
                      {inv.equipmentLabel ? (
                        <span
                          className="text-xs px-2 py-0.5 rounded-md truncate max-w-[120px] block"
                          style={{ backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
                          title={inv.equipmentLabel}
                        >
                          {inv.equipmentLabel}
                        </span>
                      ) : (
                        <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>—</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${stColor.bg} ${stColor.text}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="btn-ghost p-1.5"
                          title="Uredi intervenciju"
                          onClick={() => { setEditingInt(inv); setFormOpen(true); }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          className="btn-ghost p-1.5"
                          title="Obriši intervenciju"
                          style={{ color: 'hsl(var(--destructive))' }}
                          onClick={() => setDeleteTarget(inv)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filtered.length > 0 && (
        <div
          className="flex items-center justify-between px-6 py-3 border-t"
          style={{ borderColor: 'hsl(var(--border))', backgroundColor: 'hsl(var(--card))' }}
        >
          <div className="flex items-center gap-2 text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
            <span>Prikaži</span>
            <select
              className="input-field text-sm py-1 px-2 w-16"
              value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              {PAGE_SIZES.map(s => (
                <option key={`page-size-${s}`} value={s}>{s}</option>
              ))}
            </select>
            <span>od {filtered.length} intervencija</span>
          </div>

          <div className="flex items-center gap-1">
            <button
              className="btn-secondary text-xs py-1.5 px-3"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              ← Preth.
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const p = i + 1;
              return (
                <button
                  key={`page-${p}`}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                    page === p ? 'text-white' : ''
                  }`}
                  style={page === p
                    ? { backgroundColor: 'hsl(var(--accent))', color: 'white' }
                    : { color: 'hsl(var(--muted-foreground))' }
                  }
                >
                  {p}
                </button>
              );
            })}
            <button
              className="btn-secondary text-xs py-1.5 px-3"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Sledeća →
            </button>
          </div>
        </div>
      )}

      {/* Form modal */}
      {formOpen && (
        <InterventionForm
          open={formOpen}
          onClose={() => { setFormOpen(false); setEditingInt(null); }}
          onSave={handleSave}
          initial={editingInt}
        />
      )}

      {/* Single delete confirm */}
      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Obriši intervenciju"
        message={`Da li ste sigurni da želite da obrišete ovu intervenciju za klijenta "${deleteTarget?.clientName}"? Ova akcija se ne može poništiti.`}
        loading={deleteLoading}
      />

      {/* Bulk delete confirm */}
      <ConfirmModal
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={handleBulkDelete}
        title="Obriši izabrane intervencije"
        message={`Da li ste sigurni da želite da obrišete ${selected.size} izabranih intervencija? Ova akcija se ne može poništiti.`}
        confirmLabel={`Obriši ${selected.size} intervencija`}
        loading={deleteLoading}
      />
    </div>
  );
}